<script>
  import { onMount } from 'svelte';
  import Icon from 'fa-svelte';
  import { faCog, faSignOutAlt, faUser } from '@fortawesome/free-solid-svg-icons';
  import { mediaCluster, user } from '../../../Store/store';
  import { getIdTokenResult, logout, onAuthStateChanged } from '../../../Api/api';
  import Dialog from '../../Common/Dialog/Dialog.svelte';
  import Button from '../../Common/Button.svelte';
  import { getAdminUsers, updateAdminUsers } from '../../../Api/usersApi';
  import { scaleCluster } from '../../../Api/mediaServerApi';
  import ClusterConfig from './ClusterConfig.svelte';

  let userInitials = '';
  let menuExpanded = false;
  let settingsOpen = false;
  let userSettingsOpen = false;
  let clusterSettingsOpen = false;
  let displayName = '';
  let adminUsers = [];
  let isSuperAdmin;
  let clusterConfig;

  onMount(async () => {
    await onAuthStateChanged(async (loggedInUser) => {
      if (loggedInUser) {
        let ui = getUserInitialsFromName(loggedInUser.displayName);
        if (!ui) {
          ui = getUserInitialsFromMail(loggedInUser.email);
        }
        userInitials = ui;
        const token = await getIdTokenResult();
        isSuperAdmin = token.claims.level === 'super-admin';
        displayName = loggedInUser.displayName;
      }
    });
  });

  const getUserInitialsFromName = (name) => {
    let initials = '';
    if (name) {
      const s = name.split(' ');
      if (s.length === 1) {
        initials = s[0].substring(0, 2);
      } else if (s.length >= 2) {
        initials = s[0].charAt(0);
        initials += s[1].charAt(0);
      }
    }
    return initials;
  };

  const getUserInitialsFromMail = (mail) => {
    return mail ? mail.charAt(0) : '';
  };

  const onLogoutClicked = (e) => {
    logout().catch((error) => {
      console.log(error);
    });
  };

  const onSaveDisplayName = () => {
    $user
      .updateProfile({
        displayName: displayName,
      })
      .then(function (e) {
        userInitials = getUserInitialsFromName(displayName);
        settingsOpen = !settingsOpen;
      })
      .catch(function (error) {
        console.log('Update error', error);
        settingsOpen = !settingsOpen;
      });
  };

  const onShowSettings = () => {
    settingsOpen = !settingsOpen;
  };

  const onShowUsers = async () => {
    userSettingsOpen = !userSettingsOpen;
    adminUsers = await getAdminUsers();
  };

  const onShowCluster = async () => {
    clusterConfig = $mediaCluster.config;
    clusterSettingsOpen = true;
  };

  const onSaveUserSettings = async () => {
    userSettingsOpen = false;
    await updateAdminUsers(adminUsers);
  };

  // DEPRECATED
  const onSaveClusterSettings = async () => {
    try {
      clusterSettingsOpen = false;
      await scaleCluster(clusterConfig);
    } catch (e) {
      console.log('Scaling cluster failed with:', e);
    }
  };

  const onWindowMouseDown = () => {
    if (menuExpanded) {
      menuExpanded = false;
    }
  };
</script>

<svelte:window on:mouseup={onWindowMouseDown} />

<div class="user-container">
  <div class="user-initials" on:click={() => (menuExpanded = !menuExpanded)}>
    {userInitials}
  </div>
  <div class="user-menu" class:open={menuExpanded === true}>
    <p on:click={onShowSettings}>
      <span class="icon">
        <Icon icon={faCog} />
      </span>
      Innstillinger
    </p>
    {#if isSuperAdmin}
      <p on:click={onShowUsers}>
        <span class="icon">
          <Icon icon={faUser} />
        </span>
        Brukere
      </p>
    {/if}
    <p on:click={onLogoutClicked}>
      <span class="icon">
        <Icon icon={faSignOutAlt} />
      </span>
      Logg ut
    </p>
  </div>
</div>

<Dialog
  title="Media Server"
  open={clusterSettingsOpen}
  on:cancel={() => (clusterSettingsOpen = false)}
>
  <div slot="content" class="dialog-content">
    {#if clusterConfig}
      <ClusterConfig
        config={clusterConfig}
        edgeServerCount={clusterConfig.edge.minSize.toString()}
        onCancel={() => (clusterSettingsOpen = false)}
        onSave={onSaveClusterSettings}
      />
    {/if}
  </div>
</Dialog>

<Dialog title="Brukere" open={userSettingsOpen} on:cancel={() => (userSettingsOpen = false)}>
  <div slot="content" class="dialog-content">
    <div class="table-data">
      <table class="users">
        <thead>
          <tr>
            <th>Email</th>
            <th>Email Verified</th>
            <th>MFA</th>
            <th>Role</th>
          </tr>
        </thead>
        <tbody>
          {#each adminUsers as user, idx}
            <tr>
              <td>{user.email}</td>
              <td>{user.emailVerified}</td>
              <td>{user.mfa}</td>
              <td>
                <form>
                  <div class="role">
                    <input
                      type="radio"
                      bind:group={user.role}
                      value={'super-admin'}
                      name="role_{idx}"
                      id="role_super_admin_{idx}"
                      class="role__input"
                    />
                    <label for="role_super_admin_{idx}" class="role__label">Super Admin</label>
                  </div>
                  <div class="role">
                    <input
                      type="radio"
                      bind:group={user.role}
                      value={'admin'}
                      name="role_{idx}"
                      id="role_admin_{idx}"
                      class="role__input"
                    />
                    <label for="role_admin_{idx}" class="role__label">Admin</label>
                  </div>
                  <div class="role">
                    <input
                      type="radio"
                      bind:group={user.role}
                      value={'singleChat'}
                      name="role_{idx}"
                      id="role_singleChat_{idx}"
                      class="role__input"
                    />
                    <label for="role_singleChat_{idx}" class="role__label">Single Chat</label>
                  </div>
                  <div class="role">
                    <input
                      type="radio"
                      bind:group={user.role}
                      value={''}
                      name="role_{idx}"
                      id="role_none_{idx}"
                      class="role__input"
                    />
                    <label for="role_none_{idx}" class="role__label">Ingen tilgang</label>
                  </div>
                </form>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
    <div class="btn-container">
      <Button on:click={() => (userSettingsOpen = false)} class="cancel-button" secondary>
        Avbryt
      </Button>
      <Button class="confirm-button" on:click={onSaveUserSettings}>Ok</Button>
    </div>
  </div>
</Dialog>

<Dialog title="Innstillinger" open={settingsOpen} on:cancel={() => (settingsOpen = false)}>
  <div slot="content" class="dialog-content">
    <h3>Brukernavn</h3>
    <input type="text" bind:value={displayName} />
    <div class="btn-container">
      <Button on:click={() => (settingsOpen = false)} class="cancel-button" secondary>
        Avbryt
      </Button>
      <Button
        class="confirm-button"
        on:click={onSaveDisplayName}
        disabled={displayName?.length === 0}
      >
        Ok
      </Button>
    </div>
  </div>
</Dialog>

<style src="./user.scss">
</style>
